<template>
  <div v-if="visible" class="settings-dropdown-menu" @click.stop>
    <!-- 标题 -->
    <div class="settings-dropdown-header">
      <h3>设置</h3>
    </div>
    
    <!-- 设置内容 -->
    <div class="settings-dropdown-content">
      <!-- 语言选择 -->
      <div class="settings-section">
        <div class="settings-label">语言选择</div>
        <div class="settings-options">
          <button
            v-for="lang in languages"
            :key="lang"
            class="settings-option-btn"
            :class="{ active: settings.language === lang }"
            @click="updateSetting('language', lang)"
          >
            {{ lang }}
          </button>
        </div>
      </div>

      <!-- 排序规则 -->
      <div class="settings-section">
        <div class="settings-label">排序规则</div>
        <div class="settings-options">
          <button
            v-for="sort in sortRules"
            :key="sort.value"
            class="settings-option-btn"
            :class="{ active: settings.sortRule === sort.value }"
            @click="updateSetting('sortRule', sort.value)"
          >
            {{ sort.label }}
          </button>
        </div>
      </div>

      <!-- 比赛信息 -->
      <div class="settings-section">
        <div class="settings-label">比赛信息</div>
        <div class="settings-options settings-checkbox-group">
          <div
            v-for="info in matchInfos"
            :key="info.value"
            class="settings-checkbox-item"
            @click="toggleMatchInfo(info.value)"
          >
            <div 
              class="settings-checkbox"
              :class="{ checked: settings.matchInfo.includes(info.value) }"
            >
              <svg v-if="settings.matchInfo.includes(info.value)" class="check-icon" viewBox="0 0 16 16" fill="none">
                <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <span class="settings-checkbox-label">{{ info.label }}</span>
          </div>
        </div>
      </div>

      <!-- 事件提示 -->
      <div class="settings-section">
        <div class="settings-label">事件提示</div>
        <div class="settings-options settings-checkbox-group">
          <div
            v-for="event in eventTips"
            :key="event.value"
            class="settings-checkbox-item"
            @click="toggleEventTip(event.value)"
          >
            <div 
              class="settings-checkbox"
              :class="{ checked: settings.eventTips.includes(event.value) }"
            >
              <svg v-if="settings.eventTips.includes(event.value)" class="check-icon" viewBox="0 0 16 16" fill="none">
                <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <span class="settings-checkbox-label">{{ event.label }}</span>
          </div>
        </div>
      </div>

      <!-- 提示范围 -->
      <div class="settings-section">
        <div class="settings-label">提示范围</div>
        <div class="settings-options">
          <button
            v-for="scope in tipScopes"
            :key="scope.value"
            class="settings-option-btn"
            :class="{ active: settings.tipScope === scope.value }"
            @click="updateSetting('tipScope', scope.value)"
          >
            {{ scope.label }}
          </button>
        </div>
      </div>

      <!-- 置顶热门赛事 -->
      <div class="settings-section">
        <div class="settings-label">置顶热门赛事</div>
        <div class="settings-options">
          <button
            v-for="option in topEventOptions"
            :key="option.value"
            class="settings-option-btn"
            :class="{ active: settings.topHotEvents === option.value }"
            @click="updateSetting('topHotEvents', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  // 可以接收初始设置
  initialSettings: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:visible', 'settings-change'])

// 语言选项
const languages = ['简体', '繁体', 'English']

// 排序规则
const sortRules = [
  { label: '按时间', value: 'time' },
  { label: '按联赛', value: 'league' }
]

// 比赛信息选项
const matchInfos = [
  { label: '黄牌', value: 'yellowCard' },
  { label: '红牌', value: 'redCard' },
  { label: '排名', value: 'ranking' }
]

// 事件提示选项
const eventTips = [
  { label: '进球', value: 'goal' },
  { label: '红牌', value: 'redCard' }
]

// 提示范围选项
const tipScopes = [
  { label: '全部', value: 'all' },
  { label: '收藏', value: 'favorite' },
  { label: '关闭', value: 'close' }
]

// 置顶热门赛事选项
const topEventOptions = [
  { label: '开启', value: true },
  { label: '关闭', value: false }
]

// 设置状态
const settings = reactive({
  language: '简体',
  sortRule: 'time',
  matchInfo: ['yellowCard', 'redCard', 'ranking'], // 默认全选
  eventTips: ['goal', 'redCard'], // 默认全选
  tipScope: 'all',
  topHotEvents: false
})

/**
 * 更新简单设置项
 */
const updateSetting = (key, value) => {
  settings[key] = value
  emitSettingsChange()
}

/**
 * 切换比赛信息选项
 */
const toggleMatchInfo = (value) => {
  const index = settings.matchInfo.indexOf(value)
  if (index > -1) {
    settings.matchInfo.splice(index, 1)
  } else {
    settings.matchInfo.push(value)
  }
  emitSettingsChange()
}

/**
 * 切换事件提示选项
 */
const toggleEventTip = (value) => {
  const index = settings.eventTips.indexOf(value)
  if (index > -1) {
    settings.eventTips.splice(index, 1)
  } else {
    settings.eventTips.push(value)
  }
  emitSettingsChange()
}

/**
 * 发送设置变更事件
 */
const emitSettingsChange = () => {
  emit('settings-change', { ...settings })
  // 保存到 localStorage
  saveSettings()
}

/**
 * 保存设置到 localStorage
 */
const saveSettings = () => {
  try {
    localStorage.setItem('football-score-settings', JSON.stringify(settings))
  } catch (error) {
    console.error('保存设置失败:', error)
  }
}

/**
 * 从 localStorage 加载设置
 */
const loadSettings = () => {
  try {
    const saved = localStorage.getItem('football-score-settings')
    if (saved) {
      const parsedSettings = JSON.parse(saved)
      Object.assign(settings, parsedSettings)
    }
  } catch (error) {
    console.error('加载设置失败:', error)
  }
}

/**
 * 初始化设置
 */
const initSettings = () => {
  // 优先从 localStorage 加载
  loadSettings()
  
  // 如果有传入初始设置，则覆盖
  if (props.initialSettings && Object.keys(props.initialSettings).length > 0) {
    Object.assign(settings, props.initialSettings)
  }
}

// 初始化
initSettings()

// 监听 initialSettings 变化
watch(
  () => props.initialSettings,
  (newSettings) => {
    if (newSettings && Object.keys(newSettings).length > 0) {
      Object.assign(settings, newSettings)
    }
  },
  { deep: true }
)
</script>

<style scoped lang="scss">
.settings-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: 480px;
  background: $bg-white;
  border-radius: 10px;
  border: 1px solid $border-light;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // 标题
  .settings-dropdown-header {
    padding: 12px 18px;
    background: rgba($color-primary, 0.08);
    border-radius: 10px 10px 0 0;

    h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      color: $color-primary;
      text-align: center;
    }
  }

  // 内容区域
  .settings-dropdown-content {
    padding: 16px 18px;
    max-height: none;
    overflow: visible;

    .settings-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 14px;
      padding-bottom: 14px;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
      }

      .settings-label {
        font-size: 13px;
        font-weight: 600;
        color: $color-primary;
        min-width: 90px;
        flex-shrink: 0;
      }

      .settings-options {
        display: flex;
        gap: 6px;
        flex-wrap: nowrap;
        align-items: center;

        .settings-option-btn {
          padding: 5px 14px;
          font-size: 12px;
          font-weight: 500;
          color: $text-secondary;
          background: #f5f7fa;
          border: 1px solid transparent;
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 58px;
          text-align: center;
          white-space: nowrap;

          &:hover {
            background: $bg-hover;
            color: $color-primary;
            border-color: rgba($color-primary, 0.3);
          }

          &.active {
            background: linear-gradient(135deg, $color-primary 0%, $color-primary-dark 100%);
            color: $bg-white;
            border-color: transparent;
            font-weight: 600;

            &:hover {
              background: linear-gradient(135deg, $color-primary-dark 0%, darken($color-primary-dark, 8%) 100%);
            }
          }
        }

        // 复选框组样式
        &.settings-checkbox-group {
          display: flex;
          gap: 16px;

          .settings-checkbox-item {
            display: flex;
            align-items: center;
            cursor: pointer;
            user-select: none;

            .settings-checkbox {
              width: 16px;
              height: 16px;
              border: 2px solid #d9d9d9;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 5px;
              transition: all 0.2s ease;
              background: $bg-white;
              flex-shrink: 0;

              &.checked {
                background: linear-gradient(135deg, $color-primary 0%, $color-primary-dark 100%);
                border-color: $color-primary;

                .check-icon {
                  width: 9px;
                  height: 9px;
                  color: $bg-white;
                }
              }

              &:hover {
                border-color: $color-primary;
              }
            }

            .settings-checkbox-label {
              font-size: 12px;
              color: $text-secondary;
              font-weight: 500;
              transition: color 0.2s ease;
              white-space: nowrap;
            }

            &:hover {
              .settings-checkbox-label {
                color: $color-primary;
              }
            }
          }
        }
      }
    }
  }
}
</style>
